import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button, Card, CardContent, Grid, Typography, Container, Box, IconButton, Chip } from '@mui/material';
import { Add, Edit, Delete } from '@mui/icons-material';
import { AddonService } from 'types/service';
import { getAddonServices, deleteAddonService } from 'services/serviceService';

const AddonServiceList: React.FC = () => {
  const [addonServices, setAddonServices] = useState<AddonService[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();

  const fetchAddonServices = async () => {
    try {
      const data = await getAddonServices();
      setAddonServices(data);
      setLoading(false);
    } catch (err) {
      setError('Failed to fetch addon services');
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchAddonServices();
  }, []);

  const handleDelete = async (id: number) => {
    if (window.confirm('Are you sure you want to delete this addon service?')) {
      try {
        await deleteAddonService(id);
        await fetchAddonServices(); // Refresh the list
      } catch (err) {
        setError('Failed to delete addon service');
      }
    }
  };

  if (loading) return <div>Loading...</div>;
  if (error) return <div>Error: {error}</div>;

  return (
    <div>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4" gutterBottom fontWeight="bold">
            Addon Services
        </Typography>
        
        
        <Button
          variant="contained"
          color="primary"
          startIcon={<Add />}
          component={Link}
          to="/addon-services/new"
        >
          Add New Addon Service
        </Button>
      </Box>

      <Grid container spacing={3}>
        {addonServices.map((service) => (
          <Grid item key={service.addon_id} xs={12} sm={6} md={4}>
            <Card sx={{ height: '100%' }}>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start">
                  <div>
                    <Typography variant="h6">{service.name}</Typography>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, my: 1 }}>
                      <Chip 
                        label={`${process.env.REACT_APP_SITE_CURRENCY}${Number(service.base_price).toFixed(2)}`} 
                        color="primary" 
                        size="small" 
                      />
                      <Chip 
                        label={`${service.duration} min`} 
                        variant="outlined" 
                        size="small" 
                      />
                      <Chip 
                        label={service.status ? 'Active' : 'Inactive'} 
                        color={service.status ? 'success' : 'default'} 
                        size="small" 
                      />
                    </Box>
                    {service.description && (
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
                        {service.description}
                      </Typography>
                    )}
                    {service.name && (
                      <Typography variant="caption" color="text.secondary" display="block" sx={{ mt: 1 }}>
                        Service: {service.name}
                      </Typography>
                    )}
                  </div>
                  <div>
                    <IconButton
                      color="primary"
                      component={Link}
                      to={`/addon-services/edit/${service.addon_id}`}
                    >
                      <Edit />
                    </IconButton>
                    <IconButton
                      color="error"
                      onClick={() => handleDelete(service.addon_id)}
                    >
                      <Delete />
                    </IconButton>
                  </div>
                </Box>
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </div>
  );
};

export default AddonServiceList;